import { useTranslations } from '@vocab/react';
import {
  Badge,
  Box,
  ButtonIcon,
  ButtonLink,
  IconClear,
  Inline,
  Stack,
  Text,
} from 'braid-design-system';
import { add, isBefore, parseISO, isAfter } from 'date-fns';
import { useEffect, type FC } from 'react';

import { TRACKING_CANDIDATE_LIST } from 'src/constants/pageTrackingData';
import { Display } from 'src/libs/Display';
import {
  trackRecruiterNetworkBannerButtonPressedEvent,
  trackRecruiterNetworkBannerDisplay,
  trackRecruiterNetworkBannerDismissEvent,
} from 'src/utils/events';

import translations from './.vocab';
import { SeekRecruiterNetworkLogo } from './SeekRecruiterNetworkLogo';

import * as styles from './RecruiterNetworkAlert.css';

const allowedRecruiterNetworkClassifications: string[] = [
  '1200', // Accounting
  '6251', // Administration & Office Support
  '1206', // Construction
  '1209', // Engineering
  '6281', // Information & Communication Technology
  '1216', // Legal
  '6092', // Manufacturing, Transport & Logistics
  '6008', // Marketing & Communications
  '1220', // Real Estate & Property
  '6362', // Sales
];

const allowedRecruiterNetworkLocations: Record<string, boolean> = {
  '1000': true,
  '1002': true,
  '1004': true,
  '1007': true,
  '1009': true,
};

export const shouldShowRecruiterNetwork = (
  jobCreatedDate: string,
  classificationId: string | null | undefined,
  locationIds: string[],
): boolean => {
  const isoDate = parseISO(jobCreatedDate);

  return Boolean(
    isBefore(isoDate, add(Date.now(), { days: -12 })) &&
      isAfter(isoDate, add(Date.now(), { months: -6 })) &&
      classificationId &&
      allowedRecruiterNetworkClassifications.includes(classificationId) &&
      locationIds &&
      locationIds.filter(
        (locationId) => allowedRecruiterNetworkLocations[locationId],
      ).length !== 0,
  );
};

interface RecruiterNetworkAlertProps {
  hideAlert: () => void;
  jobCreatedDate: string;
  categoryId: string | null | undefined;
  locationIds: string[];
}

interface SRNAlertProps {
  hideAlert: () => void;
}

export const RecruiterNetworkAlert: FC<RecruiterNetworkAlertProps> = ({
  hideAlert,
  jobCreatedDate,
  categoryId,
  locationIds,
}) => {
  const showRecruiterNetwork = shouldShowRecruiterNetwork(
    jobCreatedDate,
    categoryId,
    locationIds,
  );
  if (!showRecruiterNetwork) {
    return null;
  }

  return <SRNAlert hideAlert={hideAlert} />;
};

const SRNAlert: FC<SRNAlertProps> = ({ hideAlert }) => {
  useEffect(() => {
    trackRecruiterNetworkBannerDisplay({
      currentPage: TRACKING_CANDIDATE_LIST,
    });
  }, []);

  const { t } = useTranslations(translations);

  return (
    <Box
      position="relative"
      style={{ backgroundColor: '#122F83' }}
      borderRadius="large"
      padding={['medium', 'large']}
    >
      <Box
        display="flex"
        width="full"
        justifyContent="spaceBetween"
        height="full"
        background="customDark"
      >
        <Stack space="large">
          <Inline space="small" alignY="center">
            <SeekRecruiterNetworkLogo />
            <Badge tone="positive">{t('New')}</Badge>
          </Inline>

          <Stack space="small">
            <Text size="standard" weight="strong">
              {t('Reduce how much vacancies are costing your business')}
            </Text>

            <Text size="small">
              {t(
                'Connect with experienced recruiters who specialise in filling roles like yours',
              )}
              <br />
              {t('Find out how SEEK Recruiter Network can work for you.')}
            </Text>
          </Stack>

          <Inline space="small">
            <ButtonLink
              href="https://talent.seek.com.au/recruiter-network"
              target="_blank"
              onClick={() =>
                trackRecruiterNetworkBannerButtonPressedEvent({
                  bannerElement: t('Go to SEEK Recruiter Network'),
                })
              }
              role="button"
              tone="brandAccent"
              size="small"
            >
              {t('Go to SEEK Recruiter Network')}
            </ButtonLink>
          </Inline>
        </Stack>

        <Display desktop>
          <Box
            alignItems="center"
            display="flex"
            flexGrow={1}
            flexShrink={0}
            width="full"
            paddingLeft="large"
            height="full"
          >
            <Box
              component="img"
              role="img"
              src="https://seekcdn.com/cm-ui/images/CandiMan-SRN.png"
              style={{ height: 'auto', margin: 'auto', width: '248px' }}
              alignItems="center"
              display="flex"
              flexGrow={1}
            />
          </Box>
        </Display>
      </Box>

      <Box
        className={styles.closeButtonPosition}
        display="flex"
        alignItems="center"
        justifyContent={{
          desktop: 'center',
          tablet: 'center',
          mobile: 'flexEnd',
        }}
        background="customDark"
      >
        <ButtonIcon
          id="close-srn-alert"
          icon={<IconClear />}
          label={t('Close')}
          variant="transparent"
          bleed
          onClick={() => {
            trackRecruiterNetworkBannerDismissEvent({});
            hideAlert();
          }}
        />
      </Box>
    </Box>
  );
};
